import { useState } from 'react';
import type { SeoCheck, SeoReview } from '../../../lib/aiJobSummary';
import { Badge, Button } from '../../ui';
import SectionHeading, { OpCard } from './SectionHeading';

const TONE = { pass: 'up', warn: 'warn', fail: 'down' } as const;
const GROUP: Record<string, string> = {
  keyword: 'Keyword',
  head: 'Title, meta, canonical',
  headings: 'Headings',
  images: 'Images',
  schema: 'Structured data',
  links: 'Links',
  lighthouse: 'PageSpeed (mobile Lighthouse)',
};
const faint: React.CSSProperties = { fontSize: 'var(--text-xs)', color: 'var(--text-faint)' };

function Row({ c }: { c: SeoCheck }) {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'baseline', padding: '4px 0' }}>
      <span style={{ width: '44px', flexShrink: 0 }}>
        <Badge tone={TONE[c.status]}>{c.status}</Badge>
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{c.label}</div>
        {c.detail ? <div style={{ ...faint, overflowWrap: 'anywhere' }}>{c.detail}</div> : null}
      </div>
    </div>
  );
}

/**
 * Page Build jobs: the SEO review the build ran on its page (scripts/page-seo-check.mjs). Fails and warns are listed;
 * passes fold into a count per group until opened.
 */
export default function SeoReviewCard({ review }: { review: SeoReview }) {
  const [showAll, setShowAll] = useState(false);
  const groups = [...new Set(review.checks.map((c) => c.group))];
  const { pass, warn, fail } = review.summary;
  return (
    <section>
      <SectionHeading
        eyebrow={`SEO review · ${review.url}`}
        title={fail ? `${fail} failing` : 'Passes'}
        icon="search"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Badge tone="up">{pass} pass</Badge>
            <Badge tone={warn ? 'warn' : 'neutral'}>{warn} warn</Badge>
            <Badge tone={fail ? 'down' : 'neutral'}>{fail} fail</Badge>
            <Button size="sm" variant="ghost" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Problems only' : 'Every check'}
            </Button>
          </div>
        }
      />
      {review.primary ? (
        <p style={{ margin: '0 0 var(--space-3)', ...faint }}>
          Target “{review.primary}”{review.secondary?.length ? ` · also ${review.secondary.join(', ')}` : ''}
        </p>
      ) : null}
      <OpCard tone="raised">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {groups.map((g) => {
            const all = review.checks.filter((c) => c.group === g);
            const problems = all.filter((c) => c.status !== 'pass');
            const shown = showAll ? all : problems;
            return (
              <div key={g}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="t-eyebrow">{GROUP[g] ?? g}</span>
                  <span className="t-num" style={faint}>
                    {all.length - problems.length}/{all.length} pass
                  </span>
                </div>
                {shown.map((c, i) => (
                  <Row key={`${g}-${c.id ?? i}`} c={c} />
                ))}
              </div>
            );
          })}
          {review.accepted?.length ? (
            <div>
              <span className="t-eyebrow">Warnings accepted</span>
              {review.accepted.map((a) => (
                <div key={a} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', padding: '2px 0' }}>
                  {a}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </OpCard>
    </section>
  );
}
